import { useCallback, useEffect, useRef, useState } from 'react';
import { gomailFetch, gomailLoginUrl } from './api';

const SEARCH_LIMIT = 50;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Microsoft session of the GoMail API. Microsoft refuses to be framed, so the login runs in a
 * popup; we poll /auth/me until the session cookie exists, then close the popup.
 */
export function useGoMailAuth() {
  const [state, setState] = useState({ loading: true, email: '', isAdmin: false });
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const checkSession = useCallback(async () => {
    try {
      const data = await gomailFetch('/auth/me');
      setState({ loading: false, email: data.email, isAdmin: !!data.isAdmin });
      setError(null);
      return true;
    } catch (e) {
      setState({ loading: false, email: '', isAdmin: false });
      // 401 = simply not connected yet; no HTTP status = API down or blocked (CORS).
      setError(e?.status === undefined ? 'Le service GoMail est injoignable.' : null);
      return false;
    }
  }, []);

  useEffect(() => {
    checkSession();
    return () => clearInterval(pollRef.current);
  }, [checkSession]);

  const login = useCallback(() => {
    setError(null);
    const w = 520;
    const h = 680;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(gomailLoginUrl(), 'gomail-login', `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
      setError('La fenêtre de connexion a été bloquée : autorisez les pop-ups pour ce site.');
      return;
    }
    setConnecting(true);
    let closedSince = null;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const ok = await checkSession();
      if (ok) {
        clearInterval(pollRef.current);
        setConnecting(false);
        try { popup.close(); } catch (e) { /* cross-origin */ }
        return;
      }
      if (popup.closed) {
        closedSince = closedSince ?? Date.now();
        // Leave a few seconds for the cookie to land after the popup closes.
        if (Date.now() - closedSince > 4000) {
          clearInterval(pollRef.current);
          setConnecting(false);
          setError('Connexion non finalisée. Réessayez.');
        }
      }
    }, 1500);
  }, [checkSession]);

  const logout = useCallback(async () => {
    try { await gomailFetch('/auth/logout', { method: 'POST' }); } catch (e) { /* ignore */ }
    setState({ loading: false, email: '', isAdmin: false });
  }, []);

  return { ...state, isAuthenticated: !!state.email, connecting, error, login, logout };
}

export function useCandidates(email) {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({ today: 0, this_week: 0, most_common_skill: '', not_viewed: 0, added_to_crm: 0 });
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const last = useRef({ pageSize: 20, page: 0 });

  const fetchCandidates = useCallback(async ({ pageSize, page, archived, viewed, addedToCrm } = {}) => {
    if (!email) return;
    if (pageSize !== undefined) last.current.pageSize = pageSize;
    if (page !== undefined) last.current.page = page;
    const limit = last.current.pageSize;
    setLoading(true);
    try {
      const data = await gomailFetch('/candidates', {
        params: {
          user_email: email,
          limit,
          offset: last.current.page * limit,
          archived: archived ? 1 : undefined,
          viewed,
          added_to_crm: addedToCrm,
        },
      });
      setCandidates(data?.results || []);
      setTotal(data?.total ?? 0);
    } catch (e) {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const fetchStats = useCallback(async () => {
    if (!email) return;
    try {
      setStats(await gomailFetch('/stats', { params: { user_email: email } }));
    } catch (e) { /* keep previous */ }
  }, [email]);

  const withEmail = useCallback((extra = {}) => ({ user_email: email, ...extra }), [email]);

  const actions = {
    markViewed: (id) => gomailFetch(`/candidates/${id}/viewed`, { method: 'PATCH', body: withEmail() }),
    markCrm: (id, crmUrl) => gomailFetch(`/candidates/${id}/crm`, { method: 'PATCH', body: withEmail(crmUrl ? { crm_url: crmUrl } : {}) }),
    remove: (id) => gomailFetch(`/candidates/${id}`, { method: 'DELETE', params: { user_email: email } }),
    archive: (id) => gomailFetch(`/candidates/${id}/archive`, { method: 'PATCH', body: withEmail() }),
    unarchive: (id) => gomailFetch(`/candidates/${id}/unarchive`, { method: 'PATCH', body: withEmail() }),
    bulkArchive: (ids) => gomailFetch('/candidates/bulk-archive', { method: 'POST', body: { ids } }),
    bulkDelete: (ids) => gomailFetch('/candidates/bulk-delete', { method: 'POST', body: { ids } }),
    validateLinkedin: (id) => gomailFetch(`/candidates/${id}/validate-linkedin`, { method: 'POST', body: withEmail() }),
    updateLinkedin: (id, linkedin) => gomailFetch(`/candidates/${id}/linkedin`, { method: 'PATCH', body: withEmail({ linkedin }) }),
    verifyManual: (id, url) => gomailFetch(`/candidates/${id}/verify-manual`, { method: 'POST', body: withEmail({ verified_url: url }) }),
    archivedCount: async () => (await gomailFetch('/candidates', { params: { limit: 1, offset: 0, archived: 1, user_email: email } }))?.total ?? 0,
  };

  return { candidates, setCandidates, stats, loading, total, fetchCandidates, fetchStats, actions };
}

export function useAiSearch(email) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);

  const search = useCallback(async (q) => {
    const text = (q ?? '').trim();
    if (!text || !email) return;
    setLoading(true);
    setActive(true);
    setQuery(text);
    try {
      const data = await gomailFetch('/search', { method: 'POST', body: { query: text, user_email: email, limit: SEARCH_LIMIT } });
      setResults(data?.results || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setActive(false);
  }, []);

  return { query, setQuery, results, setResults, loading, active, search, reset };
}

export function useGoMailConfig(enabled) {
  const [interval, setIntervalValue] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    gomailFetch('/config').then((d) => setIntervalValue(d?.poll_interval_minutes ?? null)).catch(() => {});
  }, [enabled]);

  const updateInterval = useCallback(async (minutes) => {
    const d = await gomailFetch('/config', { method: 'PATCH', body: { poll_interval_minutes: minutes } });
    setIntervalValue(d?.poll_interval_minutes ?? minutes);
  }, []);

  return { interval, updateInterval };
}

const toSummary = (d) => ({
  indexed: d?.indexed ?? 0,
  skipped: d?.skipped ?? 0,
  failed: d?.failed ?? 0,
  results: Array.isArray(d?.results) ? d.results : [],
});

/** Mailbox ingestion: passive status polling + manual trigger that waits for the run to settle. */
export function useIngest(enabled, onBackgroundComplete) {
  const [status, setStatus] = useState({ running: false, phase: 'idle', processed: 0, total: 0 });
  const wasRunning = useRef(false);
  const manual = useRef(false);
  const cbRef = useRef(onBackgroundComplete);
  cbRef.current = onBackgroundComplete;

  const apply = (d) => setStatus({ running: !!d?.running, phase: d?.phase || 'idle', processed: d?.processed ?? 0, total: d?.total ?? 0 });

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const d = await gomailFetch('/ingest/status');
        apply(d);
        const running = !!d?.running;
        if (wasRunning.current && !running && !manual.current) cbRef.current?.(toSummary(d));
        wasRunning.current = running;
        if (alive) timer = setTimeout(poll, running ? 1500 : 8000);
      } catch (e) {
        if (alive) timer = setTimeout(poll, 8000);
      }
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [enabled]);

  const trigger = useCallback(async (dateFrom) => {
    manual.current = true;
    setStatus({ running: true, phase: 'fetching', processed: 0, total: 0 });
    try {
      await gomailFetch('/ingest/trigger', { method: 'POST', params: { date_from: dateFrom } });
      const started = Date.now();
      let sawRunning = false;
      while (Date.now() - started < 5 * 60000) {
        await sleep(1000);
        let d;
        try { d = await gomailFetch('/ingest/status'); } catch (e) { continue; }
        apply(d);
        const running = !!d?.running;
        if (running) sawRunning = true;
        if ((sawRunning && !running) || (!sawRunning && !running && Date.now() - started > 6000)) {
          wasRunning.current = false;
          return toSummary(d);
        }
      }
      return null;
    } finally {
      manual.current = false;
    }
  }, []);

  return { ...status, trigger };
}
